import {
	geomTypeFromGeoJson,
	validateGeometry,
	type AccentColor,
	type GeoJsonGeometry,
	type GeomType,
} from "./index";

export const GEOJSON_IMPORT_MAX_FEATURES = 5000;
export const GEOJSON_IMPORT_MAX_GROUPS = 50;

export type GeoJsonImportEntryPlan = {
	geomType: GeomType;
	geometry: GeoJsonGeometry;
	title: string;
	description: string | null;
	properties: Record<string, unknown> | null;
};

export type GeoJsonImportGroupPlan = {
	key: string;
	layerTitle: string;
	accent: AccentColor;
	entries: GeoJsonImportEntryPlan[];
};

export type GeoJsonImportPlan =
	| {
			ok: true;
			name: string | null;
			groups: GeoJsonImportGroupPlan[];
			featureCount: number;
	  }
	| { ok: false; message: string; index?: number };

export const GEOJSON_STATUS_LAYERS: Record<string, { title: string; accent: AccentColor }> = {
	active: { title: "Aktiv", accent: "teal" },
	stolen: { title: "Gestohlen", accent: "rose" },
	damaged: { title: "Beschädigt", accent: "amber" },
};

const STATUS_ORDER = ["active", "stolen", "damaged"];

const IMPORT_ACCENTS: AccentColor[] = ["orange", "violet", "pink", "blue", "emerald"];

const FALLBACK_TITLES: Record<GeomType, string> = {
	point: "Punkt",
	line: "Straße",
	polygon: "Bereich",
};

const NO_STATUS_TITLE = "(ohne Status)";
const NO_LAYER_TITLE = "(ohne Ebene)";

type FeatureDraft = {
	geomType: GeomType;
	geometry: GeoJsonGeometry;
	title: string | null;
	description: string | null;
	properties: Record<string, unknown> | null;
	statusKey: string | null;
	layerKey: string | null;
};

function stringProp(props: Record<string, unknown> | null, key: string): string | null {
	const value = props?.[key];
	if (typeof value !== "string") return null;
	const trimmed = value.trim();
	return trimmed || null;
}

function featureProps(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return value as Record<string, unknown>;
}

function orderedKeys(drafts: FeatureDraft[], mode: "status" | "layer"): string[] {
	const keys: string[] = [];
	const seen = new Set<string>();
	if (mode === "status") {
		for (const status of STATUS_ORDER) {
			if (drafts.some((d) => d.statusKey === status)) {
				keys.push(status);
				seen.add(status);
			}
		}
	}
	for (const draft of drafts) {
		const key = mode === "status" ? draft.statusKey : draft.layerKey;
		if (key === null || seen.has(key)) continue;
		seen.add(key);
		keys.push(key);
	}
	if (drafts.some((d) => (mode === "status" ? d.statusKey : d.layerKey) === null)) {
		keys.push(mode === "status" ? NO_STATUS_TITLE : NO_LAYER_TITLE);
	}
	return keys;
}

export function planGeoJsonImport(fc: unknown): GeoJsonImportPlan {
	if (!fc || typeof fc !== "object") {
		return { ok: false, message: "A GeoJSON FeatureCollection is required" };
	}
	const collection = fc as { type?: unknown; features?: unknown; name?: unknown };
	if (collection.type !== "FeatureCollection") {
		return { ok: false, message: 'GeoJSON type must be "FeatureCollection"' };
	}
	if (!Array.isArray(collection.features)) {
		return { ok: false, message: "GeoJSON features must be an array" };
	}
	if (collection.features.length === 0) {
		return { ok: false, message: "GeoJSON has no features" };
	}
	if (collection.features.length > GEOJSON_IMPORT_MAX_FEATURES) {
		return {
			ok: false,
			message: `Too many features (max ${GEOJSON_IMPORT_MAX_FEATURES})`,
		};
	}
	const name =
		typeof collection.name === "string" && collection.name.trim()
			? collection.name.trim()
			: null;

	const drafts: FeatureDraft[] = [];
	for (let i = 0; i < collection.features.length; i++) {
		const feature = collection.features[i];
		if (!feature || typeof feature !== "object") {
			return { ok: false, message: "Feature must be an object", index: i };
		}
		const geometry = (feature as { geometry?: unknown }).geometry as
			| GeoJsonGeometry
			| null
			| undefined;
		if (!geometry || typeof geometry !== "object" || typeof geometry.type !== "string") {
			return { ok: false, message: "Feature geometry is required", index: i };
		}
		const geomType = geomTypeFromGeoJson(geometry.type);
		if (!geomType) {
			return {
				ok: false,
				message: `Unsupported geometry type ${geometry.type}`,
				index: i,
			};
		}
		const validated = validateGeometry(geomType, geometry);
		if (!validated.ok) {
			return { ok: false, message: validated.message, index: i };
		}
		const properties = featureProps(
			(feature as { properties?: unknown }).properties,
		);
		drafts.push({
			geomType,
			geometry: validated.geometry,
			title:
				stringProp(properties, "title") ??
				stringProp(properties, "name") ??
				stringProp(properties, "address"),
			description:
				stringProp(properties, "description") ?? stringProp(properties, "note"),
			properties,
			statusKey: stringProp(properties, "status"),
			layerKey: stringProp(properties, "layer"),
		});
	}

	const mode = drafts.some((d) => d.statusKey !== null)
		? "status"
		: drafts.some((d) => d.layerKey !== null)
			? "layer"
			: "single";

	const keys =
		mode === "single" ? [""] : orderedKeys(drafts, mode);
	if (keys.length > GEOJSON_IMPORT_MAX_GROUPS) {
		return {
			ok: false,
			message: `Too many distinct ${mode === "status" ? "status" : "layer"} values (max ${GEOJSON_IMPORT_MAX_GROUPS})`,
		};
	}

	const groups: GeoJsonImportGroupPlan[] = keys.map((key, groupIndex) => {
		const layerTitle =
			mode === "single"
				? (name ?? "Import")
				: mode === "status"
					? (GEOJSON_STATUS_LAYERS[key]?.title ?? key)
					: key;
		const known = mode === "status" ? GEOJSON_STATUS_LAYERS[key] : undefined;
		const accent =
			mode === "single"
				? "teal"
				: (known?.accent ?? IMPORT_ACCENTS[groupIndex % IMPORT_ACCENTS.length]);
		const counters: Record<GeomType, number> = { point: 0, line: 0, polygon: 0 };
		const entries: GeoJsonImportEntryPlan[] = [];
		for (const draft of drafts) {
			const draftKey =
				mode === "status" ? draft.statusKey : mode === "layer" ? draft.layerKey : null;
			const resolvedKey =
				draftKey === null
					? mode === "single"
						? key
						: mode === "status"
							? NO_STATUS_TITLE
							: NO_LAYER_TITLE
					: draftKey;
			if (resolvedKey !== key) continue;
			if (draft.title === null) counters[draft.geomType] += 1;
			entries.push({
				geomType: draft.geomType,
				geometry: draft.geometry,
				title:
					draft.title ??
					`${FALLBACK_TITLES[draft.geomType]} ${counters[draft.geomType]}`,
				description: draft.description,
				properties: draft.properties,
			});
		}
		return { key, layerTitle, accent, entries };
	});

	return { ok: true, name, groups, featureCount: drafts.length };
}
